import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronLeft, FaChevronRight, FaTimes, FaEdit } from "react-icons/fa";
import dayjs from "dayjs";

const MONTHS = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const WEEKDAYS = ["Sn", "Sl", "Rb", "Km", "Jm", "Sb", "Mg"];
const WEEK_FULL = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

export const UNIT_LABEL = { it: "IT", lab: "Lab", sarpra: "Sarpra" };
export const STATUS_LABEL = {
  planning: "Planning",
  terlaksana: "Terlaksana",
  tertunda: "Tertunda",
  batal: "Batal",
};

export const STATUS_COLOR = {
  planning: "#2563eb",
  terlaksana: "#059669",
  tertunda: "#d97706",
  batal: "#e11d48",
};

const UNIT_ORDER = ["it", "lab", "sarpra"];

const UNIT_COLOR = {
  it: "#fb7185",
  lab: "#a78bfa",
  sarpra: "#fbbf24",
};

const TONE = {
  it: {
    card: "bg-rose-50 border-rose-200 text-rose-900",
    chip: "bg-rose-100 text-rose-700",
  },
  lab: {
    card: "bg-violet-50 border-violet-200 text-violet-900",
    chip: "bg-violet-100 text-violet-700",
  },
  sarpra: {
    card: "bg-amber-50 border-amber-200 text-amber-950",
    chip: "bg-amber-100 text-amber-800",
  },
};

export function dateKey(value) {
  if (!value) return "";
  return dayjs(value).format("YYYY-MM-DD");
}

export function eventRange(item) {
  const start = dateKey(item.tgl);
  const end = dateKey(item.end) || start;
  return { start, end: end < start ? start : end };
}

export function eventsOnDate(events, key) {
  if (!key) return [];
  return events.filter((item) => {
    const { start, end } = eventRange(item);
    return key >= start && key <= end;
  });
}

function toneOf(item) {
  return TONE[item?.unit] || TONE.it;
}

function monthCells(year, month) {
  const first = dayjs(new Date(year, month, 1));
  const offset = (first.day() + 6) % 7;
  const cells = [];
  for (let i = 0; i < offset; i += 1) {
    cells.push({ date: first.subtract(offset - i, "day"), outside: true });
  }
  const total = first.daysInMonth();
  for (let day = 1; day <= total; day += 1) {
    cells.push({ date: first.date(day), outside: false });
  }
  while (cells.length % 7 !== 0) {
    cells.push({
      date: cells[cells.length - 1].date.add(1, "day"),
      outside: true,
    });
  }
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function unitsOnDay(events) {
  const present = new Set(events.map((item) => item.unit).filter(Boolean));
  const known = UNIT_ORDER.filter((unit) => present.has(unit));
  const extra = [...present].filter((unit) => !UNIT_ORDER.includes(unit));
  return [...known, ...extra];
}

function ringBackground(units) {
  const colors = units.map((unit) => UNIT_COLOR[unit] || "#94a3b8");
  if (colors.length <= 1) return colors[0] || "#94a3b8";
  const step = 100 / colors.length;
  return `conic-gradient(${colors
    .map((color, index) => `${color} ${index * step}% ${(index + 1) * step}%`)
    .join(", ")})`;
}

function notesForMonth(events, year, month) {
  const monthStart = dayjs(new Date(year, month, 1)).format("YYYY-MM-DD");
  const monthEnd = dayjs(new Date(year, month + 1, 0)).format("YYYY-MM-DD");
  return events
    .map((item) => {
      const { start, end } = eventRange(item);
      if (start > monthEnd || end < monthStart) return null;
      const visibleStart = start > monthStart ? start : monthStart;
      const visibleEnd = end < monthEnd ? end : monthEnd;
      return {
        item,
        day: dayjs(visibleStart).date(),
        endDay: dayjs(visibleEnd).date(),
        key: visibleStart,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.key.localeCompare(b.key) || a.item.id - b.item.id);
}

function MonthBoard({ year, month, events, onPickDate, selectedKey, large }) {
  const weeks = monthCells(year, month);
  const notes = notesForMonth(events, year, month);
  const today = dayjs().format("YYYY-MM-DD");

  return (
    <section className={large ? "" : "min-w-0"}>
      {!large && (
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          {MONTHS[month]}
        </h3>
      )}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((label) => (
          <div
            key={label}
            className="text-center text-[10px] font-medium tracking-wide text-slate-400"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {weeks.map((week, index) => (
          <div key={index} className="grid grid-cols-7">
            {week.map((cell) => {
              const key = cell.date.format("YYYY-MM-DD");
              const dayEvents = eventsOnDate(events, key);
              const units = unitsOnDay(dayEvents);
              const active = units.length > 0;
              const isToday = key === today;
              const isSelected = key === selectedKey;
              const size = large ? "h-9 w-9 text-sm" : "h-7 w-7 text-[11px]";
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!active}
                  onClick={() => onPickDate(key)}
                  aria-label={
                    active
                      ? `${cell.date.format("D MMMM YYYY")}, ${dayEvents.length} kegiatan`
                      : cell.date.format("D MMMM YYYY")
                  }
                  className={`coe-day mx-auto flex shrink-0 items-center justify-center self-center justify-self-center rounded-full disabled:opacity-100 ${size} ${
                    active ? "cursor-pointer" : "cursor-default"
                  } ${isSelected ? "ring-2 ring-primary ring-offset-1" : ""}`}
                  style={
                    active
                      ? { background: ringBackground(units), padding: large ? 3 : 2 }
                      : undefined
                  }
                >
                  <span
                    className={`flex h-full w-full items-center justify-center rounded-full font-medium ${
                      isToday
                        ? "bg-primary font-semibold text-white"
                        : active
                        ? "bg-white font-semibold text-slate-700"
                        : cell.outside
                        ? "text-slate-300"
                        : "text-slate-600"
                    }`}
                  >
                    {cell.date.date()}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {notes.length > 0 && (
        <ul className={`mt-2 space-y-0.5 border-t border-slate-100 pt-1.5 leading-tight ${large ? "text-sm" : "text-[11px]"}`}>
          {notes.map(({ item, day, endDay, key }) => (
            <li key={`${item.id}-${key}`}>
              <button
                type="button"
                onClick={() => onPickDate(key)}
                className="coe-note flex w-full items-baseline gap-1.5 py-px text-left leading-tight"
              >
                <span className="shrink-0 tabular-nums text-slate-400">
                  {endDay !== day ? `${day}-${endDay}` : day}
                </span>
                <span
                  className="min-w-0 flex-1 truncate font-medium"
                  style={{ color: UNIT_COLOR[item.unit] || "#64748b" }}
                >
                  {item.kegiatan}
                </span>
                <span
                  className="shrink-0 text-[10px] font-medium"
                  style={{ color: STATUS_COLOR[item.status] || "#64748b" }}
                >
                  {STATUS_LABEL[item.status] || item.status}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function CoeDatePopup({ dateKey: key, events, onClose, onEdit }) {
  const [session, setSession] = useState(null);

  useEffect(() => {
    if (key && events.length) {
      setSession({ key, events, leaving: false });
      return undefined;
    }
    setSession((current) => (current ? { ...current, leaving: true } : current));
    const timer = setTimeout(() => setSession(null), 260);
    return () => clearTimeout(timer);
  }, [key, events]);

  if (!session) return null;

  const date = dayjs(session.key);
  const title = `${WEEK_FULL[date.day()]}, ${date.date()} ${MONTHS[date.month()]} ${date.year()}`;
  const motionClass = session.leaving ? "coe-popup-out" : "coe-popup-in";
  const fadeClass = session.leaving ? "coe-fade-out" : "coe-fade-in";

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div
        className={`absolute inset-0 bg-slate-900/30 ${fadeClass}`}
        onClick={onClose}
      />
      <div
        key={session.key}
        className={`relative z-[1] w-full max-w-md rounded-3xl bg-white p-4 shadow-2xl tablet:p-5 ${motionClass}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coe-date-title"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Kegiatan pada
            </p>
            <h3 id="coe-date-title" className="text-lg font-semibold text-slate-900">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
            aria-label="Tutup"
          >
            <FaTimes />
          </button>
        </div>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {session.events.map((item) => {
            const tone = toneOf(item);
            const range = eventRange(item);
            return (
              <article
                key={item.id}
                className={`rounded-2xl border p-3 ${tone.card}`}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <h4 className="font-semibold leading-snug">{item.kegiatan}</h4>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.chip}`}>
                    {UNIT_LABEL[item.unit] || item.unit || "-"}
                  </span>
                </div>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Periode</dt>
                    <dd>
                      {dayjs(range.start).format("DD MMM YYYY")}
                      {range.end !== range.start
                        ? ` – ${dayjs(range.end).format("DD MMM YYYY")}`
                        : ""}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Status</dt>
                    <dd
                      className="font-medium"
                      style={{ color: STATUS_COLOR[item.status] || "#334155" }}
                    >
                      {STATUS_LABEL[item.status] || item.status || "-"}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Lokasi</dt>
                    <dd className="text-right">{item.lokasi || "-"}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Team</dt>
                    <dd className="text-right">{item.nama || "-"}</dd>
                  </div>
                </dl>
                {(item.ket || item.keterangan) && (
                  <p className="mt-2 text-xs leading-relaxed opacity-80">
                    <span className="font-medium">Keterangan: </span>
                    {item.ket || item.keterangan}
                  </p>
                )}
                {item.catatan && (
                  <p className="mt-2 text-xs leading-relaxed opacity-80">{item.catatan}</p>
                )}
                <button
                  type="button"
                  onClick={() => onEdit(item)}
                  className="mt-3 inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl bg-white/80 px-3 py-2 text-sm font-medium text-primary hover:bg-white"
                >
                  <FaEdit />
                  Update
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

function CoeCalendar({
  year,
  month,
  view,
  events,
  loading,
  selectedKey,
  onToday,
  onPrev,
  onNext,
  onView,
  onPickDate,
}) {
  const label =
    view === "year" ? String(year) : `${MONTHS[month]} ${year}`;

  return (
    <div className="rounded-[28px] bg-white p-3 shadow-sm tablet:p-5">
      <div className="mb-4 flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
        <button
          type="button"
          onClick={onToday}
          className="self-start rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          Hari ini
        </button>
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={onPrev}
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            aria-label="Sebelumnya"
          >
            <FaChevronLeft />
          </button>
          <p className="min-w-[9rem] text-center text-base font-semibold text-slate-800">
            {label}
          </p>
          <button
            type="button"
            onClick={onNext}
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            aria-label="Berikutnya"
          >
            <FaChevronRight />
          </button>
        </div>
        <div className="flex self-end rounded-full bg-slate-100 p-1 text-sm tablet:self-auto">
          {[
            ["year", "Tahun"],
            ["month", "Bulan"],
          ].map(([value, text]) => (
            <button
              key={value}
              type="button"
              onClick={() => onView(value)}
              className={`rounded-full px-4 py-1.5 font-medium ${
                view === value
                  ? "bg-white text-primary shadow-sm"
                  : "text-slate-500"
              }`}
            >
              {text}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {Object.entries(UNIT_LABEL).map(([unit, text]) => (
          <span key={unit} className="inline-flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: UNIT_COLOR[unit] }}
            />
            {text}
          </span>
        ))}
        <span className="text-slate-400">
          Ketuk tanggal yang bertanda untuk melihat kegiatan.
        </span>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-500">Memuat kalender COE...</div>
      ) : view === "month" ? (
        <MonthBoard
          year={year}
          month={month}
          events={events}
          onPickDate={onPickDate}
          selectedKey={selectedKey}
          large
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 tablet:grid-cols-2 xl:grid-cols-4">
          {MONTHS.map((name, index) => (
            <MonthBoard
              key={name}
              year={year}
              month={index}
              events={events}
              onPickDate={onPickDate}
              selectedKey={selectedKey}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CoeCalendar;
